import { AttentionBanner, Button, Message, PhoneField, SectionHeader, validatePhone } from '@flos/react-ui';
import { useEffect, useState } from 'react';
import { useAvailabilities } from '../contexts/AvailabilitiesContext';
import { useResourceBundle } from '../contexts/ResourceContext';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import useRefreshableScrollOnMount from '../hooks/useRefreshableScrollOnMount';
import { navigationActions } from '../store/navigation.slice';
import { submit } from '../store/navigation.thunk';
import { getDanishDayName } from '../utils/date-utils';
import { disableAgriculture } from '../utils/disable-agriculture';
import { disableCommercial } from '../utils/disable-commercial';
import { disableBuildingAndContent } from '../utils/disable-private-claims';
import log from '../utils/logger';
import FormGroup from './common/FormGroup';

const Confirmation = ({ valid = true }: { valid?: boolean }) => {
  const resource = useResourceBundle();
  const { refresh } = useAvailabilities();

  const dispatch = useAppDispatch();
  const submission = useAppSelector((state) => state.navigation.submission);
  const storedMobile = useAppSelector((state) => state.navigation.mobile);
  const primaryPhone = useAppSelector((state) => state.user.activeUser?.primaryPhone);

  const businessArea = useAppSelector((state) => state.navigation.area);
  const selectedCategory = useAppSelector((state) => state.navigation.category);
  const claimArea = useAppSelector((state) => state.navigation.selections.injury.category);

  const [disableCallbackOrdering, setDisableCallbackOrdering] = useState<boolean>(false);

  useEffect(() => {
    if (storedMobile) {
      setMobile(storedMobile);
    } else if (primaryPhone) {
      setMobile(primaryPhone);
      dispatch(navigationActions.updateMobile(primaryPhone));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If there's an error during submission, log it
  const error = submission.error;

  if (error) {
    log.error('Error in submitting to Genesys', error);
  }

  const [showInfo, setShowInfo] = useState(false);
  const [mobile, setMobile] = useState('');
  const scrollTarget = useRefreshableScrollOnMount<HTMLDivElement>({}, showInfo);

  const updateMobile = () => {
    dispatch(navigationActions.updateMobile(mobile));
  };

  useEffect(() => {
    if (businessArea === 'agriculture' && selectedCategory === 'injury') {
      if (disableAgriculture()) {
        setDisableCallbackOrdering(true);
      } else {
        setDisableCallbackOrdering(false);
      }
    }
  }, [businessArea, selectedCategory]);

  useEffect(() => {
    if (businessArea === 'private' && claimArea && selectedCategory === 'injury') {
      if (disableBuildingAndContent(claimArea)) {
        setDisableCallbackOrdering(true);
      } else {
        setDisableCallbackOrdering(false);
      }
    }
  }, [claimArea, businessArea, selectedCategory]);

  useEffect(() => {
    if (businessArea === 'commercial' && selectedCategory === 'injury') {
      if (disableCommercial()) {
        setDisableCallbackOrdering(true);
      } else {
        setDisableCallbackOrdering(false);
      }
    }
  }, [selectedCategory, businessArea]);

  return (
    <div className="u-Space-up--lg">
      {disableCallbackOrdering && (
        <Message variant="warning" className="u-spacing-stack-bottom-5">
          I dag {`${getDanishDayName(new Date())}`} kan du ikke bestille opkald. Prøv igen imorgen.
        </Message>
      )}
      {!showInfo && (
        <Button onClick={() => setShowInfo(true)} disabled={!valid || disableCallbackOrdering}>
          {resource['label.button.continue']}
        </Button>
      )}

      {showInfo && (
        <div ref={scrollTarget}>
          <SectionHeader title={resource['label.contact.header']} />
          <FormGroup>
            <PhoneField
              required={true}
              label={resource['label.contact.mobile']}
              value={mobile}
              onChange={(evt) => setMobile(evt.target.value)}
              onBlur={updateMobile}
            />
          </FormGroup>
          <div className="u-Space-up u-Space-down">
            <Button
              onClick={() =>
                dispatch(submit())
                  .unwrap()
                  .catch((error) => {
                    if (error === 'time-slot-not-available') {
                      refresh();
                    }
                  })
              }
              disabled={submission.status === 'loading' || !validatePhone(mobile) || !valid}
            >
              {resource['label.button.submit']}
            </Button>
          </div>
          {submission.status === 'error' && !submission.knownError && (
            <FormGroup className="u-Space-up">
              <AttentionBanner iconShape="error" variant="error">
                {resource['error.submission']}
              </AttentionBanner>
            </FormGroup>
          )}
          <div className="u-Space-up--lg">
            <div dangerouslySetInnerHTML={{ __html: resource['label.disclaimer'] }}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Confirmation;
